import { useEffect, useRef, useState, useCallback } from 'react';

import { Form, Formik } from 'formik';
import type { FormikProps } from 'formik';
import { AnimatePresence } from 'framer-motion';

import medicineShelf from 'public/medicine-shelf.svg';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Card, CardItem } from 'src/app/components/Elements/Card';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import { Progress } from 'src/app/components/Elements/Progress';
import Spinner from 'src/app/components/Elements/Spinner';
import { SegmentFormIncomplete } from 'src/app/components/Error/SegmentFormIncomplete';
import { useFocusOnFirstFormError } from 'src/app/components/Form/Hooks/useFocusOnFirstFormError';
import { MedicationProviderFields } from 'src/app/components/Form/MedicationFields/MedicationProviderFields';
import TextInput from 'src/app/components/Form/TextInput';
import ToggleInput from 'src/app/components/Form/ToggleInput';
import { usePatientList } from 'src/app/hooks/data/patient';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { useSegmentTrack } from 'src/app/hooks/useSegmentTrack';
import {
  fetchAccountProviders,
  getMedicalHistoryFormInitialValues,
  medicalHistoryFormIsSubmitting,
  submitPatientMedicalHistory,
} from 'src/app/state/slices/medicalHistoryForm';
import { useDispatch, useSelector } from 'src/app/state/store';
import type { MedicalHistoryFormValues, MedicalHistoryRequestValues } from 'src/app/types/medicalHistoryForm';

import { CardAnimation, CardHeaderText, ImgContainer, InputAnimation, StyledSubmitButton, StyledSVG } from './styles';
import { formValuesToAnalyticsProps, formValuesToRequest } from './utils';
import validation from './validation';

const toggleValues = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

const MedicalHistory = (props: FormikProps<MedicalHistoryFormValues>) => {
  const { values, errors, isValidating, setFieldTouched } = props;
  const { allergies, allergiesToggle, conditions, conditionsToggle, medications, noMedicationsCheckbox } = values;

  const dispatchSelectCheckoutMedicalProfileToggle = useSegmentEvent('selectCheckoutMedicalProfileToggle', {
    shouldTrackOnMount: false,
  });

  const isSubmitting = useSelector(medicalHistoryFormIsSubmitting);

  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const { data: patients, isSuccess: isPatientListSuccess } = usePatientList(
    user ? parseInt(user.accountId) : -1,
    Boolean(user)
  );
  const [primaryPatientId, setPrimaryPatientId] = useState(-1);

  const [step, setStep] = useState(1);
  const [focused, setFocused] = useState('');
  const dispatch = useDispatch();

  useEffect(() => {
    if (isPatientListSuccess && patients && patients.length > 0) {
      const primaryPatient = patients.filter(patient => patient.isPrimary);
      setPrimaryPatientId(parseInt(primaryPatient[0].id));
    }
  }, [isPatientListSuccess, patients]);

  const toggleField = useCallback(
    (fieldName: string, value: boolean) => {
      dispatchSelectCheckoutMedicalProfileToggle({ field: fieldName, value: value ? 'Yes' : 'No' });

      if (!value) {
        setFieldTouched(fieldName, false, false);
      }
    },
    [dispatchSelectCheckoutMedicalProfileToggle, setFieldTouched]
  );

  useEffect(() => {
    if (primaryPatientId > 0) {
      dispatch(fetchAccountProviders());
    }
  }, [dispatch, primaryPatientId]);
  // The step state should be advanced to the latest step that has no form values
  useEffect(() => {
    let currentStep = step;
    // medications array initialized with empty string to satisfy
    // MedicationsFieldArray's map of medications to render the fields.
    // empty string is falsy
    if (medications[0] || noMedicationsCheckbox) {
      currentStep = currentStep < 2 ? 2 : currentStep;
    }
    if (allergies || allergiesToggle !== null) {
      currentStep = currentStep < 3 ? 3 : currentStep;
    }
    if (conditions || conditionsToggle !== null) {
      currentStep = currentStep < 4 ? 4 : currentStep;
    }
    if (step !== currentStep) {
      setStep(currentStep);
    }
  }, [step, medications, noMedicationsCheckbox, allergies, allergiesToggle, conditions, conditionsToggle]);

  useEffect(() => {
    switch (step) {
      case 1:
        dispatch(segmentTrack(UNIFIED.viewOtherMedications));
        break;
      case 2:
        dispatch(segmentTrack(UNIFIED.viewAllergyQuestion));
        break;
      case 3:
        dispatch(segmentTrack(UNIFIED.viewMedicalConditionsQuestion));
        break;
      default:
        break;
    }
  }, [dispatch, step]);
  useSegmentTrack(UNIFIED.viewCheckoutAllergyProfile);

  const formRef = useRef<HTMLFormElement | null>(null);
  useFocusOnFirstFormError(formRef, errors, isSubmitting, isValidating, false);

  if (!medications) return <Spinner />;

  return (
    <StandardPage
      title={'Medical History'}
      superTitle={'1 MINUTE'}
      subtitle={'Help our pharmacists prevent any harmful interactions by answering 3 questions.'}
    >
      <ImgContainer>
        <StyledSVG src={medicineShelf} />
      </ImgContainer>

      <Form ref={formRef}>
        <SegmentFormIncomplete />
        <Card>
          <CardItem>
            <CardHeaderText variant="heading3">What other medications are you taking?</CardHeaderText>
            <MedicationProviderFields hasSkipCheckbox />
          </CardItem>
        </Card>

        {step >= 2 && (
          <CardAnimation>
            <Card addTopMarginToAllCards>
              <CardItem>
                <CardHeaderText variant="heading3">Do you have any allergies?</CardHeaderText>
                <ToggleInput
                  name="allergiesToggle"
                  options={toggleValues}
                  onClickCallback={(value: boolean) => toggleField('allergies', value)}
                />
                <AnimatePresence>
                  {allergiesToggle && (
                    <InputAnimation>
                      <TextInput
                        name="allergies"
                        onFocus={() => setFocused('allergies')}
                        onBlur={() => setFocused('')}
                        label={focused === 'allergies' || allergies ? '' : 'e.g. Penicillin'}
                        type="text"
                      />
                    </InputAnimation>
                  )}
                </AnimatePresence>
              </CardItem>
            </Card>
          </CardAnimation>
        )}
        {step >= 3 && (
          <CardAnimation>
            <Card addTopMarginToAllCards>
              <CardItem>
                <CardHeaderText variant="heading3">Do you have any medical conditions?</CardHeaderText>
                <ToggleInput
                  name="conditionsToggle"
                  options={toggleValues}
                  onClickCallback={(value: boolean) => toggleField('conditions', value)}
                />
                <AnimatePresence>
                  {conditionsToggle && (
                    <InputAnimation>
                      <TextInput
                        name="conditions"
                        onFocus={() => setFocused('conditions')}
                        onBlur={() => setFocused('')}
                        label={focused === 'conditions' || conditions ? '' : 'e.g. High blood pressure'}
                        type="text"
                      />
                    </InputAnimation>
                  )}
                </AnimatePresence>
              </CardItem>
            </Card>
          </CardAnimation>
        )}
        {step === 4 && (
          <CardAnimation>
            <StyledSubmitButton variant={'outline-lg'} isSubmitting={isSubmitting}>
              {isSubmitting && (
                <Progress
                  size={16}
                  thickness={2.5}
                  color="inherit"
                  style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }}
                />
              )}
              {!isSubmitting && <>Continue</>}
            </StyledSubmitButton>
          </CardAnimation>
        )}
      </Form>
    </StandardPage>
  );
};

export const MedicalHistoryForm = () => {
  const medicalHistoryFormInitialValues = useSelector(getMedicalHistoryFormInitialValues);
  const dispatch = useDispatch();
  const handleSubmit = useCallback(
    async values => {
      const requestValues: MedicalHistoryRequestValues = formValuesToRequest(values);
      const analyticsValues = formValuesToAnalyticsProps(values);
      await dispatch(submitPatientMedicalHistory(requestValues));
      await dispatch(segmentTrack(UNIFIED.saveCheckoutAllergyProfile, analyticsValues));
    },
    [dispatch]
  );
  return (
    <Formik
      component={MedicalHistory}
      initialValues={medicalHistoryFormInitialValues}
      enableReinitialize
      validationSchema={validation.schema}
      validateOnBlur
      onSubmit={handleSubmit}
    />
  );
};
